import React, { useCallback } from 'react';
import styled from 'styled-components';

import { useConfig, usePostMessageContext, useLinks, useFeatures } from 'src/providers';

import { ReactComponent as CoveredByClyde } from 'src/images/logos/covered-by-clyde.svg';
import { ReactComponent as LogoLockup } from 'src/images/logos/logo-lockup.svg';
import { ReactComponent as LogoShield } from 'src/images/logos/logo-shield.svg';
import { ReactComponent as LogoShieldXCover } from 'src/images/logos/logo-shield-xcover.svg';
import { ReactComponent as LogoWordmark } from 'src/images/logos/logo-wordmark.svg';
import { ReactComponent as XCover } from 'src/images/logos/xcover.svg';

export enum LogoLinkOptions {
  LOCKUP = 'lockup',
  PLAIN = 'plain',
  XPLAIN = 'xplain',
  SHIELD = 'shield',
  XSHIELD = 'xshield',
  WORDMARK = 'wordmark',
  XMODAL = 'xmodal'
}

interface IClydeLogoLinkProps {
  logoType: LogoLinkOptions;
}

const StyledClydeLogoLink = styled.a`
  line-height: 0;
  text-decoration: none;
`;

const SvgWrapper = styled.span`
  display: flex;
  align-items: center;
  > * {
    margin: 0;
    font-size: 10px;
    font-weight: 400;
    color: #000;
  }
  svg {
    width: auto;
    height: 12px;
    margin-bottom: 2px;
    margin-left: 4px;
  }
`;

const xcoverLogos : Partial<Record<LogoLinkOptions, LogoLinkOptions>> = {
  [LogoLinkOptions.PLAIN]: LogoLinkOptions.XPLAIN,
  [LogoLinkOptions.SHIELD]: LogoLinkOptions.XSHIELD,
  [LogoLinkOptions.WORDMARK]: LogoLinkOptions.XMODAL,
};

export function ClydeLogoLink(props: IClydeLogoLinkProps) {
  const { cartType } = useFeatures();
  const { type: productPageType, showXCoverBranding, phrases } = useConfig();
  const { postMessage, source } = usePostMessageContext();
  const { clydeLogo: link } = useLinks();
  const { logoType } = props;

  const brandedLogoType = showXCoverBranding ? xcoverLogos[logoType] ?? logoType : logoType;
  const svgId = 'clyde_logo_link_svg';

  const onLogoClick = useCallback(() => {
    postMessage('clydeLogoClick', {
      cartType: source === 'cart' ? cartType : undefined,
      productPageType: source === 'product' ? productPageType : undefined,
    });
  }, [postMessage]);

  const renderLogo = useCallback(() => {
    switch (brandedLogoType) {
      case LogoLinkOptions.LOCKUP:
        return <LogoLockup id={svgId} />;
      case LogoLinkOptions.PLAIN:
        return <CoveredByClyde id={svgId} />;
      case LogoLinkOptions.WORDMARK:
        return <LogoWordmark id={svgId} />;
      case LogoLinkOptions.SHIELD:
        return <LogoShield id={svgId} />;
      case LogoLinkOptions.XSHIELD:
        return <LogoShieldXCover id={svgId} />;

      default:
      case LogoLinkOptions.XPLAIN:
      case LogoLinkOptions.XMODAL:
        return (
          <SvgWrapper>
            <span>{phrases['shared.poweredBy']}</span>
            <XCover id={svgId} />
          </SvgWrapper>
        );
    }
  }, [logoType, phrases]);

  if (!link) {
    return renderLogo();
  }

  return (
    <StyledClydeLogoLink
      aria-labelledby={svgId}
      className="clydelink"
      href={link}
      id="clyde-logo-link"
      onClick={() => { onLogoClick(); }}
      rel="noreferrer"
      target="_blank"
      data-test="clyde-logo"
    >
      {renderLogo()}
    </StyledClydeLogoLink>
  );
}
